import type { FrameworkPackDefinition } from "@/lib/behavior/frameworkPacks/frameworkPackTypes";

export const EXECUTIVE_PRESENCE_PACK: FrameworkPackDefinition = {
  id: "executive_presence",
  label: "Executive Presence",
  family: "veera_core",
  source: "curated",
  preview: {
    philosophySummary:
      "Executive Presence notices how clarity, pacing, composure, and decision framing shape the signal a leader sends in important moments.",
    whatItNotices: [
      "Whether the leader's message was clear enough to guide attention and action.",
      "How pacing, structure, and decision framing shaped influence in the room.",
      "Where pressure sharpened or diluted the leadership signal.",
    ],
    watchouts: [
      "Do not judge charisma, confidence, or personality.",
      "Do not rank presence or assign effectiveness labels.",
      "Do not infer insecurity or executive readiness from a single moment.",
    ],
    sampleCoachLanguage: [
      "What signal did you intend to send in that moment?",
      "Where was the message crisp, and where did it get diluted?",
    ],
    sampleLeaderLanguage: [
      "Through this lens, this moment may highlight how clearly the leadership signal landed.",
      "A useful reflection is what the room needed to understand, decide, or feel confident moving toward.",
    ],
  },
  runtime: {
    coreConstructs: ["Signal clarity", "Composure", "Pacing", "Decision framing", "Influence"],
    diagnosticQuestions: [
      "What signal did the leader intend to send?",
      "What was clear, and what may have been diluted by pacing or structure?",
      "How did the leader frame the decision or direction for others?",
    ],
    interventionPatterns: [
      "Clarify the leadership signal.",
      "Separate message content from delivery structure.",
      "Notice pacing and decision framing without judging personality.",
    ],
    progressMarkers: [
      "The leader can state the intended signal in one sentence.",
      "Meeting moments show clearer framing of decisions and next direction.",
      "Pacing supports rather than dilutes the message.",
    ],
    stallSignals: [
      "The message contains many details but no clear leadership signal.",
      "Pacing creates ambiguity around the decision or direction.",
    ],
  },
  operationalization: {
    noticingSignals: [
      {
        id: "diluted_signal",
        label: "Diluted leadership signal",
        description: "The intended message or decision signal may have become less clear.",
        positiveIndicators: ["message", "decision", "direction", "clarity", "signal"],
        negativeIndicators: ["unclear", "too much", "ambiguous", "buried", "diffuse"],
        evidenceHints: ["not sure what we decided", "many details", "unclear next step", "mixed signals"],
        prohibitedInferences: ["low confidence", "poor presence", "not executive ready"],
      },
      {
        id: "pacing_under_pressure",
        label: "Pacing under pressure",
        description: "Pace may have shaped comprehension, confidence, or influence.",
        positiveIndicators: ["pressure", "fast", "pause", "pace", "respond"],
        negativeIndicators: ["rushed", "moved quickly", "slowed", "interrupted"],
        evidenceHints: ["rushed through", "moved quickly", "did not pause", "pressure"],
        prohibitedInferences: ["anxious", "insecure", "lacks gravitas"],
      },
    ],
    reflectionPatterns: [
      {
        id: "signal_and_pacing",
        label: "Signal and pacing reflection",
        promptTemplates: [
          "What signal did you intend to send in this moment?",
          "Where was the message crisp, and where may it have become diluted?",
          "How did pacing shape what others could understand or act on?",
        ],
        useWhen: ["diluted_signal", "pacing_under_pressure"],
        avoidWhen: ["personality_claim", "thin_evidence"],
      },
    ],
    boundaries: {
      allowed: ["signal", "clarity", "pacing", "decision framing", "influence moment"],
      prohibited: ["charisma score", "lacks confidence", "insecure", "executive readiness rating"],
      coachOnly: ["interpret executive impact", "coach gravitas", "calibrate presence to audience"],
    },
    salienceWeights: {
      diluted_signal: 1.5,
      pacing_under_pressure: 1.3,
    },
  },
};
